import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Grid, Card, CardContent, Typography, Button, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
  InputLabel, Alert, Chip, Table, TableBody, TableCell, TableHead, 
  TableRow, Paper, IconButton, Tooltip, Divider, List, ListItem, 
  ListItemText, ListItemIcon, CircularProgress
} from '@mui/material';
import {
  Colorize as InkIcon,
  MedicalServices as MedicalIcon,
  CleaningServices as CleanIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  LocalHospital as SafetyIcon,
  Build as EquipmentIcon,
  Inventory as InventoryIcon,
  TrendingUp as TrendingUpIcon,
  Notifications as NotificationsIcon,
  LocalShipping as SupplierIcon,
  Notes as NotesIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  ShoppingCart as RestockIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { UseApi } from '../Hooks/UseApi';
import { useNavigate } from 'react-router-dom';

const TattooStudioInventory = () => {
  const api = UseApi();
  const apiRef = useRef(api);
  const navigate = useNavigate();
  
  const [overview, setOverview] = useState(null);
  const [supplies, setSupplies] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openUsageDialog, setOpenUsageDialog] = useState(false);
  const [openRestockDialog, setOpenRestockDialog] = useState(false);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [selectedSupply, setSelectedSupply] = useState(null);
  const [subscriptionTier, setSubscriptionTier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openNotesDialog, setOpenNotesDialog] = useState(false);
  const [notesSupply, setNotesSupply] = useState(null);
  const [notesHistory, setNotesHistory] = useState([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  
  // Delete confirmation dialog
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [supplyToDelete, setSupplyToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  const [usageData, setUsageData] = useState({
    usage_type: 'session',
    quantity_used: 1,
    client_name: '',
    notes: ''
  });
  
  const [restockData, setRestockData] = useState({
    quantity_added: 1,
    cost_per_unit: 0,
    notes: ''
  });

  const [newSupplyData, setNewSupplyData] = useState({
    name: '',
    category: 'other',
    current_stock: 0,
    unit_type: 'individual',
    low_stock_threshold: 2,
    cost_per_unit: 0,
    supplier_name: '',
    supplier_contact: '',
    typical_order_quantity: 1,
    notes: ''
  });

  const categoryIcons = {
    ink: <InkIcon />,
    needles: <MedicalIcon />,
    safety: <SafetyIcon />,
    aftercare: <MedicalIcon />,
    equipment: <EquipmentIcon />,
    cleaning: <CleanIcon />,
    disposable: <InventoryIcon />,
    other: <InventoryIcon />
  };

  // Check if user has inventory access (Standard or higher)
  const hasInventoryAccess = ['standard', 'starter_studio', 'growth_studio'].includes(subscriptionTier);

  useEffect(() => {
    checkAccess();
  }, []);

  useEffect(() => {
    if (hasInventoryAccess) {
      fetchOverview();
      fetchSupplies();
    }
  }, [subscriptionTier]);

  const checkAccess = async () => {
    try {
      const data = await apiRef.current.fetchWithAuth('/inventory/tattoo/');
      setSupplies(Array.isArray(data) ? data : []);
      setSubscriptionTier('standard');
      setLoading(false);
    } catch (error) {
      if (error.message?.includes('403') || error.message?.includes('require')) {
        setSubscriptionTier('basic');
      }
      setLoading(false);
    }
  };

  const fetchOverview = async () => {
    try {
      const data = await apiRef.current.fetchWithAuth('/inventory/tattoo/overview/');
      setOverview(data);
      if (data.subscription_tier) {
        setSubscriptionTier(data.subscription_tier);
      }
    } catch (error) {
      console.error('Error fetching overview:', error);
    }
  };

  const fetchSupplies = async () => {
    try {
      const data = await apiRef.current.fetchWithAuth('/inventory/tattoo/');
      setSupplies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching supplies:', error);
    }
  };

  // Fetch notes history for a supply
  const fetchNotesHistory = async (supply) => {
    setLoadingNotes(true);
    try {
      // Try to fetch usage and restock history
      const historyData = await apiRef.current.fetchWithAuth(`/inventory/tattoo/${supply.id}/history/`);
      // Build notes array from history
      const allNotes = [];
      
      // Add main supply notes if exists
      if (supply.notes) {
        allNotes.push({
          type: 'item',
          note: supply.notes,
          date: supply.created_at || supply.updated_at,
          label: 'Item Notes'
        });
      }
      
      // Add usage notes
      if (historyData?.usage_logs && Array.isArray(historyData.usage_logs)) {
        historyData.usage_logs.forEach(log => {
          if (log.notes) {
            allNotes.push({
              type: 'usage',
              note: log.notes,
              date: log.created_at,
              label: `Usage - ${log.usage_type_display || log.usage_type}`,
              quantity: log.quantity_used,
              client: log.client_name
            });
          }
        });
      }
      
      // Add restock notes
      if (historyData?.restock_logs && Array.isArray(historyData.restock_logs)) {
        historyData.restock_logs.forEach(log => {
          if (log.notes) {
            allNotes.push({
              type: 'restock',
              note: log.notes,
              date: log.created_at,
              label: 'Restock',
              quantity: log.quantity_added
            });
          }
        });
      }
      
      // Sort by date (newest first)
      allNotes.sort((a, b) => new Date(b.date) - new Date(a.date));
      setNotesHistory(allNotes);
    } catch (error) {
      console.error('Error fetching notes history:', error);
      // If history endpoint doesn't exist, just show main notes
      const allNotes = [];
      if (supply.notes) {
        allNotes.push({
          type: 'item',
          note: supply.notes,
          date: supply.created_at || supply.updated_at,
          label: 'Item Notes'
        });
      }
      setNotesHistory(allNotes);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleOpenNotesDialog = (supply) => {
    setNotesSupply(supply);
    setOpenNotesDialog(true);
    fetchNotesHistory(supply);
  };

  const handleAddSupply = async () => {
    setLoading(true);
    try {
      await apiRef.current.fetchWithAuth('/inventory/tattoo/', {
        method: 'POST',
        body: JSON.stringify({
          ...newSupplyData,
          current_stock: parseInt(newSupplyData.current_stock) || 0,
          low_stock_threshold: parseInt(newSupplyData.low_stock_threshold) || 2,
          cost_per_unit: parseFloat(newSupplyData.cost_per_unit) || 0,
          typical_order_quantity: parseInt(newSupplyData.typical_order_quantity) || 1
        })
      });
      setOpenAddDialog(false);
      fetchOverview();
      fetchSupplies();
      setNewSupplyData({
        name: '',
        category: 'other',
        current_stock: 0,
        unit_type: 'individual',
        low_stock_threshold: 2,
        cost_per_unit: 0,
        supplier_name: '',
        supplier_contact: '',
        typical_order_quantity: 1,
        notes: ''
      });
      alert('Supply added successfully!');
    } catch (error) {
      console.error('Error adding supply:', error);
      alert(`Failed to add supply: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogUsage = async () => {
    setLoading(true);
    try {
      const response = await apiRef.current.fetchWithAuth('/inventory/tattoo/log-usage/', {
        method: 'POST',
        body: JSON.stringify({
          supply_id: selectedSupply.id,
          usage_type: usageData.usage_type,
          quantity_used: parseInt(usageData.quantity_used) || 1,
          client_name: usageData.client_name || '',
          notes: usageData.notes || ''
        })
      });
      setOpenUsageDialog(false);
      fetchOverview();
      fetchSupplies();
      setUsageData({ usage_type: 'session', quantity_used: 1, client_name: '', notes: '' });
      alert('Usage logged successfully!');
    } catch (error) {
      console.error('Error logging usage:', error);
      alert(`Failed to log usage: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleRestock = async () => {
    setLoading(true);
    try {
      console.log('Restock data being sent:', {
        supply_id: selectedSupply.id,
        quantity_added: parseInt(restockData.quantity_added) || 1,
        cost_per_unit: parseFloat(restockData.cost_per_unit) || 0,
        notes: restockData.notes || ''
      });
      
      const response = await apiRef.current.fetchWithAuth('/inventory/tattoo/restock/', {
        method: 'POST',
        body: JSON.stringify({
          supply_id: selectedSupply.id,
          quantity_added: parseInt(restockData.quantity_added) || 1,
          cost_per_unit: parseFloat(restockData.cost_per_unit) || 0,
          notes: restockData.notes || ''
        })
      });
      setOpenRestockDialog(false);
      fetchOverview();
      fetchSupplies();
      setRestockData({ quantity_added: 1, cost_per_unit: 0, notes: '' });
      alert('Supply restocked successfully!');
    } catch (error) {
      console.error('Error restocking supply:', error);
      alert(`Failed to restock supply: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete supply handler
  const handleDeleteSupply = async () => {
    if (!supplyToDelete) return;
    
    setDeleting(true);
    try {
      await apiRef.current.fetchWithAuth(`/inventory/tattoo/${supplyToDelete.id}/`, {
        method: 'DELETE'
      });
      setOpenDeleteDialog(false);
      setSupplyToDelete(null);
      fetchOverview();
      fetchSupplies();
      alert('Supply deleted successfully!');
    } catch (error) {
      console.error('Error deleting supply:', error);
      alert(`Failed to delete supply: ${error.message || 'Unknown error'}`);
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteConfirmation = (supply) => {
    setSupplyToDelete(supply);
    setOpenDeleteDialog(true);
  };

  const filteredSupplies = selectedCategory === 'all' 
    ? supplies 
    : supplies.filter(supply => supply.category === selectedCategory);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Get icon for note type
  const getNoteIcon = (type) => {
    switch (type) {
      case 'usage':
        return <RemoveIcon color="primary" />;
      case 'restock':
        return <RestockIcon color="success" />;
      default:
        return <NotesIcon color="info" />;
    }
  };

  // Loading state
  if (loading && subscriptionTier === null) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  // Upgrade prompt for Basic users
  if (!hasInventoryAccess) {
    return (
      <Box sx={{ p: 4, maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <InventoryIcon sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Professional Inventory Management
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Track your supplies, never run out mid-session again
          </Typography>
        </Box>

        {/* Feature Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
              <CardContent>
                <InventoryIcon sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Supply Tracking
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Track inks, needles, gloves, and all your studio supplies in one place
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
              <CardContent>
                <NotificationsIcon sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Low Stock Alerts
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Get notified before you run out so you can reorder in time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
              <CardContent>
                <TrendingUpIcon sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Expense Tracking
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monitor your supply costs and track spending over time
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ height: '100%', textAlign: 'center', p: 2 }}>
              <CardContent>
                <SupplierIcon sx={{ fontSize: 40, color: 'info.main', mb: 1 }} />
                <Typography variant="h6" gutterBottom>
                  Supplier Info
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Store supplier contacts for quick reordering when stock is low
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* CTA */}
        <Paper sx={{ p: 4, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
          <Typography variant="h5" gutterBottom fontWeight="bold">
            Available on Standard, Starter Studio & Growth Studio Plans
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
            Upgrade your plan to unlock professional inventory management and take control of your supplies
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            onClick={() => navigate('/settings')}
            sx={{ 
              bgcolor: 'white', 
              color: 'primary.main',
              '&:hover': { bgcolor: 'grey.100' }
            }}
          >
            View Upgrade Options
          </Button>
        </Paper>
      </Box>
    );
  }

  // Full inventory management for Standard+ users
  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <InventoryIcon sx={{ mr: 2, fontSize: 40 }} color="primary" />
        Tattoo Studio Supply Management
      </Typography>

      {/* Low Stock Alerts */}
      {overview?.alerts?.low_stock_supplies?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="h6">Low Stock Alert!</Typography>
          <Typography>
            These supplies are running low: {overview.alerts.low_stock_supplies.map(s => s.name).join(', ')}
          </Typography>
        </Alert>
      )}

      {/* Category Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {overview?.categories && Object.entries(overview.categories).map(([key, category]) => (
          <Grid item xs={12} sm={6} md={3} key={key}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {categoryIcons[key] || <InventoryIcon />}
                  <Typography variant="h6" sx={{ ml: 1 }}>
                    {category.name}
                  </Typography>
                </Box>
                <Typography variant="h4">{category.total_items}</Typography>
                <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                  {category.low_stock_count > 0 && (
                    <Chip 
                      label={`${category.low_stock_count} Low`} 
                      color="warning" 
                      size="small" 
                    />
                  )}
                  {category.out_of_stock_count > 0 && (
                    <Chip 
                      label={`${category.out_of_stock_count} Out`} 
                      color="error" 
                      size="small" 
                    />
                  )}
                </Box>
                {subscriptionTier === 'growth_studio' && (
                  <Typography variant="body2" color="textSecondary">
                    Value: ${category.total_value?.toFixed(2) || '0.00'}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add New Supply Button */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Filter by Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Filter by Category"
            >
              <MenuItem value="all">All Categories</MenuItem>
              <MenuItem value="ink">Tattoo Ink</MenuItem>
              <MenuItem value="needles">Needles & Cartridges</MenuItem>
              <MenuItem value="safety">Safety Supplies</MenuItem>
              <MenuItem value="aftercare">Aftercare Products</MenuItem>
              <MenuItem value="equipment">Equipment & Tools</MenuItem>
              <MenuItem value="cleaning">Cleaning Supplies</MenuItem>
              <MenuItem value="disposable">Disposable Items</MenuItem>
              <MenuItem value="other">Other Supplies</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
            disabled={loading}
          >
            Add New Supply
          </Button>
        </Box>
      </Paper>

      {/* Supplies Table */}
      <Paper>
        {filteredSupplies.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <InventoryIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary">
              No supplies added yet
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Start tracking your tattoo supplies by clicking "Add New Supply"
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
            >
              Add Your First Supply
            </Button>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Supply Name</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Stock Level</TableCell>
                <TableCell>Unit Type</TableCell>
                <TableCell>Supplier</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSupplies.map((supply) => (
                <TableRow key={supply.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {supply.name}
                      {supply.is_low_stock && (
                        <Tooltip title="Low Stock">
                          <WarningIcon color="warning" sx={{ ml: 1 }} />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{supply.category_display}</TableCell>
                  <TableCell>
                    <Chip 
                      label={`${supply.current_stock} ${supply.unit_type_display}`}
                      color={supply.is_out_of_stock ? 'error' : supply.is_low_stock ? 'warning' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{supply.unit_type_display}</TableCell>
                  <TableCell>{supply.supplier_name || '-'}</TableCell>
                  <TableCell>
                    <Tooltip title="Use Supply">
                      <IconButton 
                        color="primary" 
                        onClick={() => {
                          setSelectedSupply(supply);
                          setOpenUsageDialog(true);
                        }}
                        disabled={loading}
                        size="small"
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Restock">
                      <IconButton 
                        color="success" 
                        onClick={() => {
                          setSelectedSupply(supply);
                          setOpenRestockDialog(true);
                        }}
                        disabled={loading}
                        size="small"
                      >
                        <AddIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View Notes History">
                      <IconButton 
                        color="info" 
                        onClick={() => handleOpenNotesDialog(supply)}
                        disabled={loading}
                        size="small"
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Supply">
                      <IconButton 
                        color="error" 
                        onClick={() => openDeleteConfirmation(supply)}
                        disabled={loading}
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      {/* Add New Supply Dialog */}
      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Supply</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Supply Name"
                fullWidth
                value={newSupplyData.name}
                onChange={(e) => setNewSupplyData({...newSupplyData, name: e.target.value})}
                required
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={newSupplyData.category}
                  onChange={(e) => setNewSupplyData({...newSupplyData, category: e.target.value})}
                  label="Category"
                >
                  <MenuItem value="ink">Tattoo Ink</MenuItem>
                  <MenuItem value="needles">Needles & Cartridges</MenuItem>
                  <MenuItem value="safety">Safety Supplies</MenuItem>
                  <MenuItem value="aftercare">Aftercare Products</MenuItem>
                  <MenuItem value="equipment">Equipment & Tools</MenuItem>
                  <MenuItem value="cleaning">Cleaning Supplies</MenuItem>
                  <MenuItem value="disposable">Disposable Items</MenuItem>
                  <MenuItem value="other">Other Supplies</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Initial Stock"
                type="number"
                fullWidth
                value={newSupplyData.current_stock}
                onChange={(e) => setNewSupplyData({...newSupplyData, current_stock: e.target.value})}
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Unit Type</InputLabel>
                <Select
                  value={newSupplyData.unit_type}
                  onChange={(e) => setNewSupplyData({...newSupplyData, unit_type: e.target.value})}
                  label="Unit Type"
                >
                  <MenuItem value="bottle">Bottle</MenuItem>
                  <MenuItem value="pack">Pack/Box</MenuItem>
                  <MenuItem value="individual">Individual Item</MenuItem>
                  <MenuItem value="tube">Tube</MenuItem>
                  <MenuItem value="roll">Roll</MenuItem>
                  <MenuItem value="case">Case</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Low Stock Threshold"
                type="number"
                fullWidth
                value={newSupplyData.low_stock_threshold}
                onChange={(e) => setNewSupplyData({...newSupplyData, low_stock_threshold: e.target.value})}
                helperText="Alert when stock falls below this number"
                inputProps={{ min: 0 }}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Cost Per Unit"
                type="number"
                inputProps={{ step: "0.01", min: 0 }}
                fullWidth
                value={newSupplyData.cost_per_unit}
                onChange={(e) => setNewSupplyData({...newSupplyData, cost_per_unit: e.target.value})}
                helperText="For expense tracking"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Supplier Name"
                fullWidth
                value={newSupplyData.supplier_name}
                onChange={(e) => setNewSupplyData({...newSupplyData, supplier_name: e.target.value})}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                label="Supplier Contact"
                fullWidth
                value={newSupplyData.supplier_contact}
                onChange={(e) => setNewSupplyData({...newSupplyData, supplier_contact: e.target.value})}
                helperText="Phone/website to reorder"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={newSupplyData.notes}
                onChange={(e) => setNewSupplyData({...newSupplyData, notes: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSupply} disabled={loading || !newSupplyData.name}>
            Add Supply
          </Button>
        </DialogActions>
      </Dialog>

      {/* Usage Dialog */}
      <Dialog open={openUsageDialog} onClose={() => setOpenUsageDialog(false)}>
        <DialogTitle>Log Supply Usage - {selectedSupply?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Usage Type</InputLabel>
                <Select
                  value={usageData.usage_type}
                  onChange={(e) => setUsageData({...usageData, usage_type: e.target.value})}
                  label="Usage Type"
                >
                  <MenuItem value="session">Tattoo Session</MenuItem>
                  <MenuItem value="setup">Station Setup</MenuItem>
                  <MenuItem value="cleanup">Cleanup</MenuItem>
                  <MenuItem value="maintenance">Equipment Maintenance</MenuItem>
                  <MenuItem value="waste">Expired/Damaged</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Quantity Used"
                type="number"
                fullWidth
                value={usageData.quantity_used}
                onChange={(e) => setUsageData({...usageData, quantity_used: e.target.value})}
                helperText={`Available: ${selectedSupply?.current_stock || 0}`}
                inputProps={{ min: 1, max: selectedSupply?.current_stock || 999 }}
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Client Name"
                fullWidth
                value={usageData.client_name}
                onChange={(e) => setUsageData({...usageData, client_name: e.target.value})}
                helperText="Optional - for session tracking"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={usageData.notes}
                onChange={(e) => setUsageData({...usageData, notes: e.target.value})}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUsageDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleLogUsage} disabled={loading}>
            Log Usage
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restock Dialog */}
      <Dialog open={openRestockDialog} onClose={() => setOpenRestockDialog(false)}>
        <DialogTitle>Restock Supply - {selectedSupply?.name}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                label="Quantity Added"
                type="number"
                fullWidth
                value={restockData.quantity_added}
                onChange={(e) => setRestockData({...restockData, quantity_added: e.target.value})}
                inputProps={{ min: 1 }}
              />
            </Grid>
            
            <Grid item xs={6}>
              <TextField
                label="Cost Per Unit"
                type="number"
                inputProps={{ step: "0.01", min: 0 }}
                fullWidth
                value={restockData.cost_per_unit}
                onChange={(e) => setRestockData({...restockData, cost_per_unit: e.target.value})}
                helperText="For expense tracking"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                label="Notes"
                fullWidth
                multiline
                rows={2}
                value={restockData.notes}
                onChange={(e) => setRestockData({...restockData, notes: e.target.value})}
                placeholder="Supplier info, batch number, etc."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenRestockDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRestock} disabled={loading}>
            Add Stock
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete Supply
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone.
          </Alert>
          <Typography>
            Are you sure you want to delete <strong>{supplyToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will also delete all usage and restock history for this item.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleDeleteSupply}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notes History Dialog */}
      <Dialog 
        open={openNotesDialog} 
        onClose={() => setOpenNotesDialog(false)} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
          <HistoryIcon sx={{ mr: 1 }} color="info" />
          Notes History - {notesSupply?.name}
        </DialogTitle>
        <DialogContent>
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : notesHistory.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <NotesIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography color="text.secondary">
                No notes recorded for this item
              </Typography>
            </Box>
          ) : (
            <List>
              {notesHistory.map((note, index) => (
                <React.Fragment key={index}>
                  <ListItem alignItems="flex-start">
                    <ListItemIcon sx={{ mt: 1 }}>
                      {getNoteIcon(note.type)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle2" color="primary">
                            {note.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(note.date)}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography 
                            variant="body2" 
                            sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}
                          >
                            {note.note}
                          </Typography>
                          {(note.quantity || note.client) && (
                            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                              {note.quantity && (
                                <Chip 
                                  size="small" 
                                  label={`Qty: ${note.quantity}`}
                                  variant="outlined"
                                />
                              )}
                              {note.client && (
                                <Chip 
                                  size="small" 
                                  icon={<PersonIcon />}
                                  label={note.client}
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < notesHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNotesDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TattooStudioInventory;